import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import axios from 'axios'

// Auth credentials for login
export interface AuthCredentials {
  clientId: string
  clientSecret: string
}

// Token response from backend auth endpoint
export interface TokenResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

// Auth context state
interface AuthContextType {
  isAuthenticated: boolean
  token: string | null
  credentials: AuthCredentials | null
  login: (credentials: AuthCredentials) => Promise<void>
  logout: () => void
  refreshToken: () => Promise<void>
  loading: boolean
  error: string | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const STORAGE_KEY = 'orchestrator_auth'
const TOKEN_KEY = 'orchestrator_token'
const REFRESH_KEY = 'orchestrator_refresh_token'

// Get backend API URL - defaults to relative path for Vite proxy
const getApiUrl = (): string => {
  const envURL = import.meta.env.VITE_API_URL
  if (envURL && envURL.trim().length) return envURL.replace(/\/$/, '')
  return '/api/v1'  // Uses Vite proxy in dev
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [credentials, setCredentials] = useState<AuthCredentials | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check for existing session on mount
  useEffect(() => {
    const storedCredentials = localStorage.getItem(STORAGE_KEY)
    const storedToken = localStorage.getItem(TOKEN_KEY)
    
    if (storedCredentials && storedToken) {
      try {
        const creds = JSON.parse(storedCredentials) as AuthCredentials
        setCredentials(creds)
        setToken(storedToken)
        setIsAuthenticated(true)
      } catch {
        // Invalid stored data, clear it
        localStorage.removeItem(STORAGE_KEY)
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(REFRESH_KEY)
      }
    }
    setLoading(false)
  }, [])

  // Login via backend auth endpoint
  const login = async (creds: AuthCredentials) => {
    setLoading(true)
    setError(null)

    try {
      const authUrl = `${getApiUrl()}/auth/login`
      
      // Send credentials to backend
      const response = await axios.post<TokenResponse>(authUrl, {
        client_id: creds.clientId,
        client_secret: creds.clientSecret,
      })

      const { access_token, refresh_token } = response.data

      console.log('Login successful - storing tokens')

      const accessTokenTrimmed = typeof access_token === 'string' ? access_token.trim() : null
      const refreshTokenTrimmed = typeof refresh_token === 'string' ? refresh_token.trim() : null

      if (!accessTokenTrimmed || !refreshTokenTrimmed) {
        throw new Error('Login response missing tokens')
      }

      // Store credentials and tokens (trim to prevent whitespace issues)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(creds))
      localStorage.setItem(TOKEN_KEY, accessTokenTrimmed)
      localStorage.setItem(REFRESH_KEY, refreshTokenTrimmed)

      setCredentials(creds)
      setToken(accessTokenTrimmed)
      setIsAuthenticated(true)
    } catch (err: any) {
      const message = err.response?.data?.detail
        || err.response?.data?.message 
        || err.message 
        || 'Authentication failed'
      setError(message)
      throw new Error(message)
    } finally {
      setLoading(false)
    }
  }

  // Logout and clear session
  const logout = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem(TOKEN_KEY)
      if (storedToken) {
        // Notify backend to invalidate session
        await axios.post(`${getApiUrl()}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${storedToken}` }
        })
      }
    } catch {
      // Ignore logout errors
    } finally {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(REFRESH_KEY)
      setCredentials(null)
      setToken(null)
      setIsAuthenticated(false)
      setError(null)
    }
  }, [])

  // Refresh the access token
  const refreshToken = useCallback(async () => {
    const storedRefresh = localStorage.getItem(REFRESH_KEY)
    if (!storedRefresh) {
      throw new Error('No refresh token available')
    }
    
    try {
      const refreshUrl = `${getApiUrl()}/auth/refresh`
      const response = await axios.post<TokenResponse>(refreshUrl, {
        refresh_token: storedRefresh,
      })
      
      const { access_token, refresh_token: newRefresh } = response.data

      console.log('Context refresh successful - storing tokens')
      const accessTokenTrimmed = typeof access_token === 'string' ? access_token.trim() : null
      const refreshTokenTrimmed = typeof newRefresh === 'string' ? newRefresh.trim() : null

      if (!accessTokenTrimmed || !refreshTokenTrimmed) {
        logout()
        throw new Error('Refresh response missing tokens')
      }

      localStorage.setItem(TOKEN_KEY, accessTokenTrimmed)
      localStorage.setItem(REFRESH_KEY, refreshTokenTrimmed)
      setToken(accessTokenTrimmed)
    } catch (err: any) {
      // If refresh fails, log out
      logout()
      throw new Error('Session expired. Please log in again.')
    }
  }, [logout])

  // Auto-refresh token before it expires
  useEffect(() => {
    if (!isAuthenticated) return

    // Refresh token 5 minutes before expiry (3600s - 300s = 3300s = 55 min)
    const REFRESH_INTERVAL = 55 * 60 * 1000 // 55 minutes in milliseconds
    
    const refreshInterval = setInterval(async () => {
      try {
        console.log('Auto-refreshing token...')
        await refreshToken()
      } catch (err) {
        console.error('Auto-refresh failed:', err)
        // The refreshToken function handles logout on failure
      }
    }, REFRESH_INTERVAL)

    return () => clearInterval(refreshInterval)
  }, [isAuthenticated, refreshToken])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        token,
        credentials,
        login,
        logout,
        refreshToken,
        loading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
